import { useEffect, useState } from 'react';
import { api, ApiCallError } from '../api/client';
import { formatMoney } from '@xiaopeiban/shared';
import type { ChildSummary, Chore } from '@xiaopeiban/shared';

export default function ChoresPage() {
  const [children, setChildren] = useState<ChildSummary[]>([]);
  const [childId, setChildId] = useState<number | null>(null);
  const [chores, setChores] = useState<Chore[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [points, setPoints] = useState('5');
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'once'>('daily');
  const [autoApprove, setAutoApprove] = useState(false);
  const [gatesPlay, setGatesPlay] = useState(false);
  const [deadlineHour, setDeadlineHour] = useState<string>(''); // '' = no deadline

  async function load() {
    try {
      const r = await api.listChildren();
      setChildren(r.children);
      if (r.children.length > 0) {
        const id = childId ?? r.children[0].id;
        setChildId(id);
        const c = await api.listChores(id);
        setChores(c.chores);
      }
    } catch (e) { setErr(niceError(e)); }
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (childId == null) return;
    api.listChores(childId).then(r => setChores(r.chores)).catch(e => setErr(niceError(e)));
  }, [childId]);

  async function createChore() {
    if (childId == null) return;
    if (!title.trim()) { setErr('请填标题'); return; }
    const pRm = parseFloat(points);
    if (!Number.isFinite(pRm) || pRm < 0.01) { setErr('奖励金额至少 RM 0.01'); return; }
    const pSen = Math.round(pRm * 100);
    try {
      const deadline = deadlineHour === '' ? null : Number(deadlineHour);
      await api.createChore({
        child_id: childId,
        title: title.trim(),
        description: description.trim() || undefined,
        points: pSen,
        frequency,
        auto_approve: autoApprove,
        daily_deadline_hour: frequency === 'daily' ? deadline : null,
        gates_play: frequency === 'daily' && gatesPlay,
      });
      setShowNew(false);
      setTitle(''); setDescription(''); setPoints('5');
      setFrequency('daily'); setAutoApprove(false);
      setGatesPlay(false); setDeadlineHour('');
      const c = await api.listChores(childId);
      setChores(c.chores);
    } catch (e) { setErr(niceError(e)); }
  }

  async function toggleActive(c: Chore) {
    try {
      await api.updateChore(c.id, { active: c.active ? 0 : 1 });
      if (childId) {
        const r = await api.listChores(childId);
        setChores(r.chores);
      }
    } catch (e) { setErr(niceError(e)); }
  }

  async function deleteChore(c: Chore) {
    if (!confirm(`确定删除"${c.title}"？`)) return;
    try {
      await api.deleteChore(c.id);
      if (childId) {
        const r = await api.listChores(childId);
        setChores(r.chores);
      }
    } catch (e) { setErr(niceError(e)); }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">🧹 家务管理</div>
        <div className="page-subtitle">给孩子布置日常家务，完成可赚马币</div>
      </div>

      {err && <div className="error-box">{err}</div>}

      {children.length > 1 && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>选择孩子</div>
          <div className="flex" style={{ flexWrap: 'wrap' }}>
            {children.map(c => (
              <button
                key={c.id}
                className={`btn ${c.id === childId ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setChildId(c.id)}
              >
                {c.avatar || '👶'} {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontWeight: 700 }}>家务清单（{chores.length}）</div>
          <button className="btn btn-primary" onClick={() => setShowNew(!showNew)}>
            {showNew ? '取消' : '+ 新建'}
          </button>
        </div>

        {showNew && (
          <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 12, marginBottom: 12 }}>
            <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>标题</label>
            <input placeholder="例如 刷牙" value={title} onChange={e => setTitle(e.target.value)} />

            <label style={{ fontWeight: 700, display: 'block', marginTop: 10, marginBottom: 4 }}>详细说明（可选）</label>
            <input placeholder="早晚各一次" value={description} onChange={e => setDescription(e.target.value)} />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
              <div>
                <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>奖励 (RM)</label>
                <input type="number" step="0.01" value={points} onChange={e => setPoints(e.target.value)} placeholder="例如 5 或 0.50" />
              </div>
              <div>
                <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>频率</label>
                <select value={frequency} onChange={e => setFrequency(e.target.value as 'daily' | 'weekly' | 'once')}>
                  <option value="daily">每日</option>
                  <option value="weekly">每周</option>
                  <option value="once">一次</option>
                </select>
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 14 }}>
              <input
                type="checkbox"
                checked={autoApprove}
                onChange={e => setAutoApprove(e.target.checked)}
                style={{ width: 'auto' }}
              />
              自动批准（孩子点完成立刻给分，不用我审批）
            </label>

            {frequency === 'daily' && (
              <div style={{
                marginTop: 12, padding: 10, borderRadius: 10,
                background: '#fff7ed', border: '1px solid #fed7aa',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={gatesPlay}
                    onChange={e => setGatesPlay(e.target.checked)}
                    style={{ width: 'auto' }}
                  />
                  <b>必须先完成才能玩游戏</b>（未做完 → 阻止聊天和购物）
                </label>
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    每日截止时间（孩子本地时区）
                  </label>
                  <select
                    value={deadlineHour}
                    onChange={e => setDeadlineHour(e.target.value)}
                  >
                    <option value="">无截止时间</option>
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={String(h)}>
                        {String(h).padStart(2, '0')}:00 前
                      </option>
                    ))}
                  </select>
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    超过这个时间未完成 → 任务变红色"已超时"，但仍可补做。
                  </div>
                </div>
              </div>
            )}

            <button className="btn btn-primary btn-block" style={{ marginTop: 12 }} onClick={createChore}>
              添加家务
            </button>
          </div>
        )}

        {chores.length === 0 && <div className="muted" style={{ textAlign: 'center', padding: 16 }}>还没有家务</div>}

        {chores.map(c => (
          <div key={c.id} style={{ padding: 12, borderBottom: '1px solid var(--border)' }}>
            <div className="flex" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 700 }}>
                  {c.title}
                  <span className="tag" style={{ marginLeft: 8 }}>{c.frequency === 'daily' ? '每日' : c.frequency === 'weekly' ? '每周' : '一次'}</span>
                  <span className="muted" style={{ marginLeft: 8 }}>+{formatMoney(c.points)}</span>
                  {c.auto_approve ? <span className="tag" style={{ marginLeft: 8, background: '#dcfce7', color: '#166534' }}>自动</span> : null}
                  {c.gates_play === 1 ? (
                    <span className="tag" style={{ marginLeft: 8, background: '#fde68a', color: '#78350f' }}>
                      🔒 玩前必做{c.daily_deadline_hour != null ? ` · ${String(c.daily_deadline_hour).padStart(2, '0')}:00 前` : ''}
                    </span>
                  ) : null}
                  {!c.active ? <span className="tag" style={{ marginLeft: 8, background: '#fee2e2', color: '#991b1b' }}>已停用</span> : null}
                </div>
                {c.description && <div className="muted" style={{ marginTop: 4 }}>{c.description}</div>}
              </div>
              <div className="flex">
                <button className="btn btn-secondary" onClick={() => toggleActive(c)}>
                  {c.active ? '停用' : '启用'}
                </button>
                <button className="btn btn-danger" onClick={() => deleteChore(c)}>删除</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function niceError(e: unknown): string {
  if (e instanceof ApiCallError) return e.message;
  return (e as Error)?.message || '出错了';
}
